/**
 * useOrbDocTutorialsAdvancedFullAppARDocFooterLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait OrbDocTutorialsAdvancedFullAppARDocFooter
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { emitCascadeEvent, useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, OrbDocTutorialsAdvancedFullAppARDocView as OrbDocTutorialsAdvancedFullAppARDocViewEntity, OrbDocTutorialsAdvancedFullAppARDocFooterEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type OrbDocTutorialsAdvancedFullAppARDocFooterState =
  | 'idle';

export type OrbDocTutorialsAdvancedFullAppARDocFooterEvent =
  | 'INIT'
  | 'FOOTER_LINK'
  | 'FOOTER_LINK_CLICKED';

type OrbDocTutorialsAdvancedFullAppARDocFooterSlot = 'main';
interface OrbDocTutorialsAdvancedFullAppARDocFooterInternalState {
  machineState: OrbDocTutorialsAdvancedFullAppARDocFooterState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsAdvancedFullAppARDocFooterEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsAdvancedFullAppARDocFooterSlot, OrbDocTutorialsAdvancedFullAppARDocFooterEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsAdvancedFullAppARDocFooterEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsAdvancedFullAppARDocViewEntity>;
}

type OrbDocTutorialsAdvancedFullAppARDocFooterAction =
  | { type: 'EVENT_SUCCESS'; event: OrbDocTutorialsAdvancedFullAppARDocFooterEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: OrbDocTutorialsAdvancedFullAppARDocFooterSlot[]; renderClear?: OrbDocTutorialsAdvancedFullAppARDocFooterSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: OrbDocTutorialsAdvancedFullAppARDocFooterEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof OrbDocTutorialsAdvancedFullAppARDocViewEntity & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function orbDocTutorialsAdvancedFullAppARDocFooterReducer(
  state: OrbDocTutorialsAdvancedFullAppARDocFooterInternalState,
  action: OrbDocTutorialsAdvancedFullAppARDocFooterAction
): OrbDocTutorialsAdvancedFullAppARDocFooterInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as OrbDocTutorialsAdvancedFullAppARDocFooterState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<OrbDocTutorialsAdvancedFullAppARDocViewEntity>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<OrbDocTutorialsAdvancedFullAppARDocViewEntity>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<OrbDocTutorialsAdvancedFullAppARDocViewEntity>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: OrbDocTutorialsAdvancedFullAppARDocFooterInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface OrbDocTutorialsAdvancedFullAppARDocFooterConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  brandName: string;
  columns: Array<{ links: Array<{ href: string; label: string } | { href: string[]; label: string[] }>; title: string[] } | { links: { href: string; label: string }[]; title: string[] } | { links: { href: string[]; label: string[] }[]; title: string[] }>;
  copyright?: string;
  edgeColor: 'background' | 'muted' | 'surface' | 'gradient' | 'inverse';
  edgeTop: 'none' | 'curve' | 'wave' | 'arc' | 'tilt' | 'step' | 'scallop';
  legalLinks: { href: string; label: string }[];
  surface: 'plain' | 'inverse';
  tagline?: string;
  texture: boolean;
}

const DEFAULT_ORB_DOC_TUTORIALS_ADVANCED_FULL_APP_AR_DOC_FOOTER_CONFIG = {
  brandName: "Orb",
  columns: [{ links: [{ href: ["str/concat", "/ar", "/docs/getting-started/introduction"], label: ["i18n/t", "std-orb-chrome:footer.start"] }, { href: ["str/concat", "/ar", "/docs"], label: ["i18n/t", "std-orb-chrome:nav.docs"] }, { href: ["str/concat", "/ar", "/blog"], label: ["i18n/t", "std-orb-chrome:nav.blog"] }], title: ["i18n/t", "std-orb-chrome:footer.learn"] }, { links: [{ href: ["str/concat", "/ar", "/playground"], label: ["i18n/t", "std-orb-chrome:nav.playground"] }, { href: ["str/concat", "/ar", "/stdlib"], label: ["i18n/t", "std-orb-chrome:nav.stdlib"] }, { href: ["str/concat", "/ar", "/explore"], label: ["i18n/t", "std-orb-chrome:footer.explore"] }, { href: ["str/concat", "/ar", "/play"], label: ["i18n/t", "std-orb-chrome:footer.play"] }, { href: ["str/concat", "/ar", "/downloads"], label: ["i18n/t", "std-orb-chrome:footer.downloads"] }, { href: "https://studio.almadar.io", label: "Studio" }], title: ["i18n/t", "std-orb-chrome:footer.tools"] }, { links: [{ href: "https://github.com/almadar-io", label: "GitHub" }, { href: "https://discord.gg/9apmY9FGVr", label: "Discord" }, { href: "https://almadar.io", label: "Almadar" }], title: ["i18n/t", "std-orb-chrome:footer.community"] }],
  edgeColor: "background",
  edgeTop: "none",
  legalLinks: [],
  surface: "plain",
  texture: false,
} as OrbDocTutorialsAdvancedFullAppARDocFooterConfig;

export interface OrbDocTutorialsAdvancedFullAppARDocFooterLogicReturn {
  state: OrbDocTutorialsAdvancedFullAppARDocFooterState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsAdvancedFullAppARDocFooterEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsAdvancedFullAppARDocFooterSlot, OrbDocTutorialsAdvancedFullAppARDocFooterEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsAdvancedFullAppARDocFooterEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsAdvancedFullAppARDocViewEntity>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbDocTutorialsAdvancedFullAppARDocFooterEventPayloadMap]: (payload?: OrbDocTutorialsAdvancedFullAppARDocFooterEventPayloadMap[K]) => Promise<EventResponse> };
  config: OrbDocTutorialsAdvancedFullAppARDocFooterConfig;
}

export function useOrbDocTutorialsAdvancedFullAppARDocFooterLogic(config?: Partial<OrbDocTutorialsAdvancedFullAppARDocFooterConfig>): OrbDocTutorialsAdvancedFullAppARDocFooterLogicReturn {
  const mergedConfig: OrbDocTutorialsAdvancedFullAppARDocFooterConfig = { ...DEFAULT_ORB_DOC_TUTORIALS_ADVANCED_FULL_APP_AR_DOC_FOOTER_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/orb-doc-tutorials-advanced-full-app-ar-doc-footer', orbitalName: 'OrbDocTutorialsAdvancedFullAppAR', traitName: 'OrbDocTutorialsAdvancedFullAppARDocFooter' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(orbDocTutorialsAdvancedFullAppARDocFooterReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['OrbDocTutorialsAdvancedFullAppARDocView'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'FOOTER_LINK') {
      // idle → idle (on FOOTER_LINK)
        _emits.push(((_p) => () => { eventBus.emit('FOOTER_LINK_CLICKED', _p, { trait: 'OrbDocTutorialsAdvancedFullAppARDocFooter' }); emitCascadeEvent(eventBus, 'OrbDocTutorialsAdvancedFullAppAR', 'OrbDocTutorialsAdvancedFullAppARDocFooter', { event: 'FOOTER_LINK_CLICKED', payload: _p, source: { orbital: 'OrbDocTutorialsAdvancedFullAppAR', trait: 'OrbDocTutorialsAdvancedFullAppARDocFooter' } }); })({ label: payloadObj?.label, href: payloadObj?.href }));
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbDocTutorialsAdvancedFullAppARDocFooterSlot[]; clear: OrbDocTutorialsAdvancedFullAppARDocFooterSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocTutorialsAdvancedFullAppARDocFooterSlot[], clear: [] as OrbDocTutorialsAdvancedFullAppARDocFooterSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'OrbDocTutorialsAdvancedFullAppARDocView': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as OrbDocTutorialsAdvancedFullAppARDocFooterEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'OrbDocTutorialsAdvancedFullAppARDocFooter': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbDocTutorialsAdvancedFullAppARDocFooterEventPayloadMap]: (payload?: OrbDocTutorialsAdvancedFullAppARDocFooterEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'FOOTER_LINK': (payload?: OrbDocTutorialsAdvancedFullAppARDocFooterEventPayloadMap['FOOTER_LINK']) => dispatch('FOOTER_LINK', payload),
    'FOOTER_LINK_CLICKED': (payload?: OrbDocTutorialsAdvancedFullAppARDocFooterEventPayloadMap['FOOTER_LINK_CLICKED']) => dispatch('FOOTER_LINK_CLICKED', payload),
  };

  useUIEvents(enqueueEvent, 'OrbDocTutorialsAdvancedFullAppAR.OrbDocTutorialsAdvancedFullAppARDocFooter', ['INIT', 'FOOTER_LINK'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('OrbDocTutorialsAdvancedFullAppARDocFooter', (): TraitStateSnapshot => ({
      traitName: 'OrbDocTutorialsAdvancedFullAppARDocFooter',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT', 'FOOTER_LINK', 'FOOTER_LINK_CLICKED'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  mergedConfig.copyright = mergedConfig.copyright ?? (__t("std-orb-chrome:footer.copyright"));
  mergedConfig.tagline = mergedConfig.tagline ?? (__t("std-orb-chrome:footer.tagline"));
  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
